import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Tables from "layouts/tables";
import { useState, useEffect } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography, CircularProgress } from "@mui/material";
import {
  useGetScansQuery,
  useDeleteScanMutation,
  useGetScanReportQuery,
  useGetSkinAiChargesQuery,
  useUpdateSkinAiChargesMutation,
} from "slices/skin/scanSlice";
import Report from "utility/report";
import { useSelector } from "react-redux";
import { hasActionAccess } from "utility";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { IMAGE_URL } from "network";
import ExportCSV from "utility/exportCsv";

function Scans() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [scanId, setScanId] = useState(null);
  const [chargesOpen, setChargesOpen] = useState(false);
  const [chargesInput, setChargesInput] = useState("");
  const [chargesError, setChargesError] = useState("");

  const { data: chargesData, refetch: refetchCharges } = useGetSkinAiChargesQuery();
  const [updateSkinAiCharges, { isLoading: updatingCharges }] = useUpdateSkinAiChargesMutation();

  const handleOpenCharges = () => {
    setChargesInput(chargesData?.skinAiCharges ?? "");
    setChargesError("");
    setChargesOpen(true);
  };

  const handleChargesChange = (e) => {
    const val = e.target.value;
    if (val !== "" && !/^\d+$/.test(val)) return;
    setChargesInput(val);
    const num = Number(val);
    if (val === "") setChargesError("Value is required");
    else if (num < 1) setChargesError("Minimum value is 1");
    else if (num > 100) setChargesError("Maximum value is 100");
    else setChargesError("");
  };

  const handleUpdateCharges = async () => {
    const num = Number(chargesInput);
    if (!chargesInput || num < 1 || num > 100) return;
    await updateSkinAiCharges(num);
    refetchCharges();
    setChargesOpen(false);
  };

  const auth = useSelector((state) => state.auth.auth?.user);

  const csvColumns = [
    { label: "Name", key: "name" },
    { label: "Phone", key: "phone" },
    { label: "Age", key: "age" },
    { label: "Gender", key: "gender" },
    { label: "Skin Score", key: "skinScore" },
    { label: "Skin Type", key: "skinType" },
    { label: "Concern", key: "skinTypeConcern" },
    { label: "Confidence", key: "skinTypeConfidence" },
    { label: "Created At", key: "createdAt" },
  ];

  const { data } = useGetScansQuery({
    page,
    limit,
    search,
  });

  // Fetch single scan report
  const { data: reportApiData } = useGetScanReportQuery(scanId?._id, {
    skip: !scanId?._id,
  });

  /* =========================
     GENERATE PDF WHEN DATA ARRIVES
     ========================= */
  useEffect(() => {
    if (!reportApiData || !scanId) return;

    const loadImage = (url) =>
      new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = url;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          canvas.width = img.width;
          canvas.height = img.height;

          ctx.drawImage(img, 0, 0);

          resolve(canvas.toDataURL("image/jpeg"));
        };
      });

    const generatePDF = async () => {
      const reportData = {
        ...scanId,
        ...reportApiData,
      };

      const pdf = new jsPDF("p", "mm", "a4");
      let y = 15;

      pdf.setFontSize(20);
      pdf.text("Skin Analysis Report", 105, y, { align: "center" });
      y += 10;

      // USER IMAGE
      if (reportData.image) {
        const img = await loadImage(IMAGE_URL + reportData.image);

        const imgWidth = 40;
        const imgHeight = 40;

        const pageWidth = pdf.internal.pageSize.getWidth();
        const x = (pageWidth - imgWidth) / 2;

        pdf.addImage(img, "JPEG", x, 25, imgWidth, imgHeight);
        // push content below image
        y = 65;
      }
      // USER INFO
      autoTable(pdf, {
        startY: y,
        theme: "grid",
        head: [["Field", "Value"]],
        body: [
          ["Name", reportData.name],
          ["Phone", reportData.phone],
          ["Email", reportData.email || "-"],
          ["Gender", reportData.gender],
          ["Age", reportData.age],
          ["Skin Score", `${reportData.skinScore}/100`],
          ["Skin Type", reportData.skinType],
          ["Primary Concern", reportData.skinTypeConcern],
          ["Confidence", reportData.skinTypeConfidence],
          ["Skin Tone ITA", reportData.basicInfo?.skintoneITA?.toFixed(2)],
          ["Skin Tone Level", reportData.basicInfo?.skintoneLevel?.label],
          ["Hue Angle", reportData.basicInfo?.hueAngle?.toFixed(2)],
          ["Undertone", reportData.basicInfo?.undertone?.label],
          ["Skin Age", reportData.basicInfo?.skin_age],
        ],
      });

      y = pdf.lastAutoTable.finalY + 10;

      // SKIN CONCERNS
      pdf.setFontSize(16);
      pdf.text("Skin Concerns", 14, y);

      autoTable(pdf, {
        startY: y + 5,
        head: [["Concern", "Severity", "Percentage"]],
        body:
          reportData.concerns?.map((c) => [
            c.name,
            c.severity,
            `${c.percentage}%`,
          ]) || [],
      });

      y = pdf.lastAutoTable.finalY + 10;

      // =========================
      // PRODUCTS WITH IMAGE
      // =========================
      if (reportData?.recommendations?.products?.length) {
        pdf.setFontSize(16);
        pdf.text("Recommended Services", 14, y);

        const products = await Promise.all(
          reportData.recommendations.products.map(async (p) => {
            const img = p.image ? await loadImage(IMAGE_URL + p.image) : null;

            return {
              name: p.name,
              image: img,
            };
          }),
        );

        autoTable(pdf, {
          startY: y + 5,
          head: [["Image", "Product"]],
          body: products.map((p) => ["", p.name]),

          didDrawCell: (data) => {
            if (data.column.index === 0 && data.cell.section === "body") {
              const img = products[data.row.index].image;

              if (img) {
                pdf.addImage(
                  img,
                  "JPEG",
                  data.cell.x + 2,
                  data.cell.y + 2,
                  12,
                  12,
                );
              }
            }
          },
        });

        y = pdf.lastAutoTable.finalY + 10;
      }

      // =========================
      // SERVICES WITH IMAGE
      // =========================
      if (reportData?.recommendations?.services?.length) {
        pdf.setFontSize(16);
        pdf.text("Recommended Products", 14, y);

        const services = await Promise.all(
          reportData.recommendations.services.map(async (s) => {
            const img = s.thumbnail
              ? await loadImage(IMAGE_URL + s.thumbnail)
              : null;

            return {
              name: s.name,
              image: img,
            };
          }),
        );

        autoTable(pdf, {
          startY: y + 5,
          head: [["Image", "Service"]],
          body: services.map((s) => ["", s.name]),

          didDrawCell: (data) => {
            if (data.column.index === 0 && data.cell.section === "body") {
              const img = services[data.row.index].image;

              if (img) {
                pdf.addImage(
                  img,
                  "JPEG",
                  data.cell.x + 2,
                  data.cell.y + 2,
                  12,
                  12,
                );
              }
            }
          },
        });
      }

      pdf.save(`scan-report-${reportData.name}.pdf`);
      setScanId(null);
    };

    generatePDF();
  }, [reportApiData]);

  /* =========================
     DOWNLOAD BUTTON
     ========================= */
  const handleDownload = (row) => {
    setScanId(row);
  };

  const col = [
    {
      display: "Image",
      key: "image",
      type: "image",
      style: { width: "100px", height: "100px", borderRadius: "20%" },
    },
    { display: "Name", key: "name", type: "display" },
    { display: "Mobile", key: "phone", type: "display" },
    { display: "Age", key: "age", type: "display" },
    { display: "Gender", key: "gender", type: "display" },
    { display: "Skin-Score", key: "skinScore", type: "display" },
    { display: "Skin-Type", key: "skinType", type: "display" },
    { display: "Concern Type", key: "skinTypeConcern", type: "display" },
    { display: "Confidence", key: "skinTypeConfidence", type: "display" },
  ];

  const canEdit = hasActionAccess(auth, "Skin-ai", "Scans", "view");
  const canDelete = hasActionAccess(auth, "Skin-ai", "Scans", "delete");

  if (canEdit || canDelete) {
    col.push({
      display: "Actions",
      key: "edit",
      type: "action",
      render: (row) => (
        <>
          {canEdit && (
            <Button
              onClick={() => {
                setEditing(row);
                setOpen(true);
              }}
            >
              View
            </Button>
          )}

          <Button onClick={() => handleDownload(row)}>Download</Button>
        </>
      ),
    });
  }

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  const changePage = (newPage, newLimit) => {
    setPage(newPage + 1);
    setLimit(newLimit);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <ExportCSV
          data={data?.data?.data}
          columns={csvColumns}
          fileName="scan-report"
        />
        <Button
          variant="contained"
          onClick={handleOpenCharges}
          sx={{ background: "#442141", color: "#fff", textTransform: "none", fontWeight: 600, "&:hover": { background: "#5a2d57" } }}
        >
          Scan Charges
        </Button>
      </Box>

      {/* Charges Modal */}
      <Dialog open={chargesOpen} onClose={() => setChargesOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#3690ed" }}>Skin AI Scan Charges</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Current charge: <strong>₹{chargesData?.skinAiCharges ?? "—"}</strong>
          </Typography>
          <TextField
            label="New Charge (₹)"
            value={chargesInput}
            onChange={handleChargesChange}
            fullWidth
            inputProps={{ inputMode: "numeric" }}
            error={!!chargesError}
            helperText={chargesError || "Enter a value between 1 and 100"}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setChargesOpen(false)} sx={{ color: "#3690ed" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateCharges}
            disabled={!!chargesError || chargesInput === "" || updatingCharges}
            sx={{ background: "#442141", "&:hover": { background: "#3690ed" } }}
          >
            {updatingCharges ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
      <Report datattt={editing} open={open} onClose={handleClose} />

      <Tables
        title={"Scans"}
        handleSearch={handleSearchChange}
        totalPages={data?.data?.pagination?.totalPages}
        data={data?.data?.data}
        columns={col}
        changePage={changePage}
        currentPage={page}
        rowsPerPage={limit}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Scans;
